import React from "react";
import { Link } from "react-router-dom";
import { UserVideo, VideoStatus } from "../lib/library-store";
import SkillBadges from "./SkillBadges";

interface Props {
  video: UserVideo;
  onRemove: (youtubeVideoId: string) => void;
}

const STATUS_LABEL: Record<VideoStatus, { label: string; tone: string }> = {
  bookmarked: { label: "Bookmarked", tone: "bg-gray-500" },
  transcribing: { label: "Transcribing", tone: "bg-amber-400 animate-pulse" },
  "quota-exceeded": { label: "Try tomorrow", tone: "bg-gray-500" },
  ready: { label: "Ready", tone: "bg-sky-400" },
  "in-progress": { label: "In progress", tone: "bg-orange-400" },
  complete: { label: "Complete", tone: "bg-emerald-400" },
};

export default function VideoCard({ video, onRemove }: Props) {
  const status = STATUS_LABEL[video.status];
  const hasTags =
    !!video.skillCategory ||
    !!video.difficulty ||
    (video.techniques?.length ?? 0) > 0;

  return (
    <Link
      to={`/library/${video.youtubeVideoId}`}
      data-testid="video-card"
      className="group relative block bg-gray-900 border border-gray-800 rounded-lg overflow-hidden transition hover:border-orange-500/60 hover:bg-gray-900/80"
    >
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/60 to-transparent opacity-0 group-hover:opacity-100 transition"
      />

      <div className="relative aspect-video bg-gray-950 overflow-hidden">
        <img
          src={video.thumbnailUrl}
          alt={video.title}
          loading="lazy"
          className="w-full h-full object-cover transition group-hover:opacity-90"
        />
      </div>

      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-medium text-gray-100 leading-snug line-clamp-2 flex-1">
            {video.title}
          </h3>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove(video.youtubeVideoId);
            }}
            title="Remove from library"
            aria-label={`Remove ${video.title} from library`}
            className="text-gray-600 hover:text-red-400 transition shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100"
          >
            ✕
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-2">{video.channel}</p>

        <span
          className="inline-flex items-center gap-1.5 text-[11px] font-display tracking-[0.18em] uppercase text-gray-400"
          title={status.label}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${status.tone}`}
            aria-hidden
          />
          {status.label}
        </span>

        {hasTags ? (
          <div className="mt-2.5">
            <SkillBadges
              skillCategory={video.skillCategory ?? null}
              difficulty={video.difficulty ?? null}
              techniques={video.techniques ?? []}
              variant="card"
            />
          </div>
        ) : null}
      </div>
    </Link>
  );
}
